// app/frontend/Pages/DanceStyles/NewDanceStyle.tsx

import React, { useState } from 'react';
import { useForm } from '@mantine/form';
import { Inertia } from '@inertiajs/inertia';
import { usePage } from '@inertiajs/react';
import { IconUpload, IconPhoto, IconX } from '@tabler/icons-react';
import { TextInput, Button, Box, Text, Group } from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import FormLayout from '../../components/FormLayout';
import ImageCropper from '../../components/ImageCropper';

import '@mantine/dropzone/styles.css';
import 'cropperjs/dist/cropper.css';

// Define the shape of the props (optional but recommended if using TypeScript)
interface DanceStyleProps {
  dance_style?: {
    name?: string;
    slug?: string;
  };
  errors?: string[]; // or a more structured type
}

function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // replace whitespace with dashes
    .replace(/[^\w-]+/g, ''); // remove non-word chars if you want
}

export default function NewDanceStyle() {
  const { dance_style, errors } = usePage<InertiaPageProps<DanceStyleProps>>().props;

  // State for storing the selected file as Base64 (for cropping)
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);

  // Mantine form setup
  const form = useForm({
    initialValues: {
      name: dance_style?.name || '',
      slug: '',
    },
    validate: {
      name: (value) => (value ? null : 'Name is required'),
    },
    validateInputOnBlur: true,
  });

  // Auto-generate slug from "name"
  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newName = event.target.value;
    form.setFieldValue('name', newName);
    form.setFieldValue('slug', slugify(newName));
  };

  // Called when user drops an image in the Dropzone
  const handleDrop = (files: File[]) => {
    if (files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        if (typeof e.target?.result === 'string') {
          setImageSrc(e.target.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Called when the image is cropped
  function handleCropped(blob: Blob) {
    console.log('Cropped blob:', blob);
    setCroppedBlob(blob);
  }

  // Form submit handler
  function handleSubmit(values: typeof form.values) {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
    const data = new FormData();
    data.append('dance_style[name]', values.name);
    data.append('dance_style[slug]', values.slug || '');

    console.log('Cropped blob: ', croppedBlob);
    if (croppedBlob) {
      data.append('dance_style[seo_image]', croppedBlob, 'dance_style.jpg');
    }

    Inertia.post('/dance_styles', data, {
      headers: {
        'X-CSRF-Token': csrfToken || '',
      },
      onError: (err) => {
        console.error('Error creating dance style:', err);
      },
    });
  }

  return (
    <FormLayout title="Create A New Dance Style">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        {/* Dropzone - call handleDrop to actually set the base64 preview */}
        <Dropzone
          onDrop={handleDrop}
          onReject={(files) => console.log('rejected files', files)}
          maxSize={5 * 1024 ** 2}
          accept={IMAGE_MIME_TYPE}
          className="mb-4"
          styles={() => ({
            root: {
              backgroundColor: '#fcefe1',
              border: '3px dashed #f6b73c',
            },
          })}
        >
          <Group justify="center" gap="md" mih={100} style={{ pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconUpload size={52} color="var(--mantine-color-blue-2)" stroke={1.5} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={52} color="var(--mantine-color-red-6)" stroke={1.5} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconPhoto size={52} color="var(--mantine-color-orange-3)" stroke={1.5} />
            </Dropzone.Idle>
            <div>
              <Text size="xl" inline>
                Drag image here or click to select file
              </Text>
              <Text size="sm" c="dimmed" inline mt={7}>
                Image must be under 5 MB
              </Text>
            </div>
          </Group>
        </Dropzone>

        {/* If we have a selected image, show the cropper */}
        {imageSrc && <ImageCropper src={imageSrc} setCroppedBlob={handleCropped} aspectRatio={16 / 9} />}

        <TextInput
          label="Dance Style Name"
          placeholder="e.g. Salsa, Kizomba..."
          className="mt-4"
          required
          value={form.values.name}
          onChange={handleNameChange}
        />

        <TextInput
          label="Slug"
          mt="md"
          value={form.values.slug}
          onChange={(e) => form.setFieldValue('slug', e.target.value)}
          className="text-muted"
        />

        <Button type="submit" fullWidth className="mt-4" size="lg">
          Create Dance Style
        </Button>
      </form>
    </FormLayout>
  );
}
